import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/useAuth";
import { AuthAlert, AuthField, AuthLayout, AuthSubmit } from "./AuthLayout";

/** State the Register flow attaches when redirecting here after a
 *  204-style signup submission — we surface the banner once and
 *  prefill the email so the user can sign in immediately.
 *
 *  `from` is set by `RequireAuth` when an unauthenticated request
 *  hits a protected route — we send the user back there after sign
 *  in instead of dumping them on the default Overview, so a pasted
 *  link to (say) `/12345678/ads/campaigns/2143241934` re-opens
 *  exactly that URL after auth. */
type LoginNavState =
  | { banner?: string; email?: string; from?: string }
  | undefined;

/** Достаточно, чтобы отсечь пустое и «строку без собаки». Строже не
 *  делаем: адрес всё равно проверяет сервер, а излишне жёсткая маска
 *  на клиенте отсекает живые адреса с плюсом и точками. */
function looksLikeEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as LoginNavState) ?? undefined;
  const [email, setEmail] = useState(navState?.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // One-shot banner from the post-signup redirect. Local state so
  // typing into the form doesn't dismiss it but a successful
  // sign-in (which navigates away) clears it naturally.
  const [banner, setBanner] = useState<string | null>(navState?.banner ?? null);

  // Where to land after the form succeeds. `from` from RequireAuth
  // wins; otherwise "/" + RootRedirect picks the user's default org.
  // We sanitise `from` to refuse anything that looks like an open
  // redirect (must start with "/" and not "//", which browsers treat
  // as schema-relative).
  const safeFrom =
    navState?.from && navState.from.startsWith("/") && !navState.from.startsWith("//")
      ? navState.from
      : null;

  if (!loading && user) {
    if (user.must_change_password) {
      return <Navigate to="/change-password" replace />;
    }
    // Already signed in (e.g. user navigated back to /login) — honour
    // the `from` if present, else let RootRedirect pick the home.
    return <Navigate to={safeFrom ?? "/"} replace />;
  }

  // Показываем сообщения полей только после первой попытки отправки:
  // подсвечивать «неверный адрес» человеку, который ещё печатает
  // первую букву, — это ругань, а не помощь.
  const emailError = touched && !looksLikeEmail(email) ? "Enter a valid email address." : null;
  const passwordError = touched && !password ? "Enter your password." : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (!looksLikeEmail(email) || !password) return;
    setSubmitting(true);
    try {
      await login(email, password);
      // Post-login: send the user back to the URL they were trying
      // to reach when RequireAuth bounced them here. Falls through
      // to "/" → RootRedirect → /<slug>/overview when no `from` was
      // set (regular sign-in from /login or /).
      navigate(safeFrom ?? "/", { replace: true });
    } catch {
      setError("Email or password is incorrect.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title={
        <>
          Welcome to <span className="em">OneAds</span>
        </>
      }
      subtitle="Log in to your account."
    >
      {banner && (
        <div className="au-note" role="status">
          <span style={{ flex: 1 }}>{banner}</span>
          <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
      {error && <AuthAlert>{error}</AuthAlert>}
      <form className="au-card" onSubmit={onSubmit} noValidate>
        <AuthField
          id="login-email"
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="username"
          placeholder="you@company.com"
          disabled={submitting}
          error={emailError}
        />
        <AuthField
          id="login-password"
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          placeholder="••••••••"
          disabled={submitting}
          error={passwordError}
        />
        <AuthSubmit busy={submitting} busyLabel="Logging in">
          Log in
        </AuthSubmit>
      </form>
      <div className="au-links">
        <Link to="/forgot-password">Forgot password?</Link>
      </div>
    </AuthLayout>
  );
}
